import moment from 'moment';
import { T } from 'react-translator-component';

moment.locale('pt-br');

export const timeChartConfiguration = {
  options: {
    title: {
      text: '',
      align: 'left',
    },
    chart: {
      id: 'basic-bar',
    },
    colors: ['#0341B6'],
    xaxis: {
      type: 'datetime',
      categories: [
        '09/01/2020 00:00',
        '09/01/2020 01:00',
        '09/01/2020 02:00',
        '09/01/2020 03:00',
        '09/01/2020 04:00',
        '09/02/2020 05:00',
        '09/03/2020 04:00',
        '09/04/2020',
        '09/05/2020',
        '09/06/2020 04:00',
        '09/07/2020 04:00',
        '09/08/2020 04:00',
        '09/09/2020 04:00',
        '09/10/2020 04:00',
      ],
      labels: {
        datetimeUTC: false,
        datetimeFormatter: {
          year: 'yyyy',
          month: "MM 'yy",
          day: 'dd MM',
          hour: 'HH:mm',
        },
        format: 'dd/MM/YYYY HH:mm',
        formatter: function (value, timestamp) {
          return moment(new Date(timestamp)).format('DD/MM/YY');
        },
      },
    },
    yaxis: {
      labels: {
        formatter: function (val) {
          return val + ' h';
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: 2,
      curve: 'smooth',
    },
  },
  series: [
    {
      name: 'Tempo de Recarga',
      data: [30, 15, 10, 5, 20, 40, 45, 50, 49, 60, 70, 91, 80, 120],
    },
  ],
};

export const POTENCY_SERIES_NAME = 'Potência';
export const SOC_SERIES_NAME = 'SoC';

export const POTENCY_SERIES_COLOR = '#0341B6';
export const SOC_SERIES_COLOR = '#F5A623';

export const potencyYAxisConfiguration = {
  seriesName: POTENCY_SERIES_NAME,
  labels: {
    formatter: function (val) {
      return parseFloat(val).toFixed(2) + ' kW';
    },
  },
};

/**
 * Em gráficos de eixo Y único o ApexCharts escala o eixo colado nos dados. Ao
 * passar dois eixos ele muda de estratégia e ancora a escala no zero, o que
 * achata a curva de potência (0–12 kW em vez de 10,01–11,95 kW). Fixar min/max
 * a partir dos próprios valores devolve o enquadramento original.
 */
export const buildPotencyYAxisConfiguration = (values) => {
  const numbers = values.map(Number).filter((value) => !Number.isNaN(value));

  if (!numbers.length) {
    return potencyYAxisConfiguration;
  }

  const min = numbers.reduce((a, b) => Math.min(a, b), Infinity);
  const max = numbers.reduce((a, b) => Math.max(a, b), -Infinity);
  const range = max - min;
  const padding = range === 0 ? 0.5 : range * 0.05;

  return {
    ...potencyYAxisConfiguration,
    min: min - padding,
    max: max + padding,
    tickAmount: 3,
  };
};

const NO_MEASUREMENT_LABEL = '-';

export const socYAxisConfiguration = {
  seriesName: SOC_SERIES_NAME,
  opposite: true,
  min: 0,
  max: 100,
  // 5 intervalos entre 0 e 100 => rótulos de 20 em 20%.
  tickAmount: 5,
  labels: {
    /**
     * Sem `tooltip` configurado, o ApexCharts reaproveita este formatter também
     * no tooltip — e ali o valor chega nulo nos instantes em que o carregador
     * não reportou SoC. Como parseFloat(null) é NaN, o formatter cru escrevia
     * "NaN %" no lugar do dado ausente; o traço deixa claro que não houve
     * medição em vez de inventar um número.
     */
    formatter: function (val) {
      const number = parseFloat(val);

      return Number.isFinite(number)
        ? number.toFixed(0) + ' %'
        : NO_MEASUREMENT_LABEL;
    },
  },
};

export const mockedPotencyChartConfiguration = {
  options: {
    title: {
      text: 'Gráfico de Potencia',
      align: 'left',
    },
    chart: {
      id: 'basic-bar',
    },
    colors: [POTENCY_SERIES_COLOR],
    xaxis: {
      type: 'datetime',
      categories: [],
      labels: {
        datetimeUTC: false,
        datetimeFormatter: {
          year: 'yyyy',
          month: "MM 'yy",
          day: 'dd MM',
          hour: 'HH:mm',
        },
        format: 'dd/MM/YYYY HH:mm',
        formatter: function (value, timestamp, index) {
          return moment(new Date(timestamp)).format('HH:mm');
        },
      },
    },
    yaxis: potencyYAxisConfiguration,
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: 2,
      curve: 'smooth',
    },
  },
  series: [],
};

export const energyChartConfiguration = {
  options: {
    title: {
      text: 'Energia consumida (kWh)',
      align: 'left',
    },
    chart: {
      id: 'basic-bar',
    },
    colors: ['#2E93fA'],
    xaxis: {
      type: 'datetime',
      categories: [
        '09/01/2020',
        '09/02/2020',
        '09/03/2020',
        '09/04/2020',
        '09/05/2020',
        '09/06/2020',
        '09/07/2020',
        '09/08/2020',
        '09/09/2020',
        '09/10/2020',
      ],
      labels: {
        datetimeUTC: false,
        datetimeFormatter: {
          year: 'yyyy',
          month: "MM 'yy",
          day: 'dd MM',
          hour: 'HH:mm',
        },
        format: 'dd/MM/YYYY HH:mm',
        formatter: function (value, timestamp, index) {
          return moment(new Date(timestamp)).format('DD MMM HH:mm');
        },
      },
    },
    yaxis: {
      labels: {
        formatter: function (val) {
          return val + ' kWh';
        },
      },
    },
  },
  series: [
    {
      name: 'Energia consumida',
      data: [30, 40, 45, 50, 49, 60, 70, 91, 80, 120],
    },
  ],
};

export const chargesChartConfiguration = {
  options: {
    title: {
      text: T('Quantity of Recharges'),
      align: 'left',
    },
    chart: {
      id: 'basic-bar',
    },
    colors: ['#00FF00'],
    xaxis: {
      type: 'datetime',
      categories: [
        '09/01/2020',
        '09/02/2020',
        '09/03/2020',
        '09/04/2020',
        '09/05/2020',
        '09/06/2020',
        '09/07/2020',
        '09/08/2020',
        '09/09/2020',
        '09/10/2020',
      ],
      labels: {
        datetimeUTC: false,
        datetimeFormatter: {
          year: 'yyyy',
          month: "MM 'yy",
          day: 'dd MM',
          hour: 'HH:mm',
        },
        format: 'dd/MM/YYYY HH:mm',
        formatter: function (value, timestamp) {
          return moment(new Date(timestamp)).format('DD MMM HH:mm');
        },
      },
    },
  },
  series: [
    {
      name: T('Recharges'),
      data: [30, 40, 45, 50, 49, 60, 70, 91, 80, 120],
    },
  ],
};
